import comms from '../../comms';
import { offlineStorage, ActionPriority } from '../../../services/offlineStorage';
import { connectionManager } from '../../../services/connectionManager';

/**
 * Itinerary Socket Helper
 * 
 * Manages socket communication for itinerary-based dispatch using ITINERARY_ACTION messages.
 * Similar to fixedSocketHelper but adapted for route/route_step itinerary format.
 * 
 * DECEMBER 2025 REFACTOR:
 * - Replaced module-level isInitialized with socket ID tracking to handle socket instance changes
 * - Added action timeout with automatic route refresh fallback to prevent stuck trips
 * - Integrated route data persistence for session recovery
 * - Updated trip matching to use TripId/reservation.id instead of TripNo
 * 
 * JANUARY 2026 REFACTOR:
 * - Added offline-first capability with store-and-forward
 * - Actions are queued when offline with original timestamps
 * - Optimistic UI updates applied immediately
 * - Server-wins conflict resolution on reconnect
 */

// Track initialized socket by ID to detect socket instance changes
let initializedSocketId = null;

// Track pending actions for timeout fallback
const pendingActions = new Map();
const ACTION_TIMEOUT_MS = 10000; // 10 seconds

// Pending signature sends, keyed by tripId.
//
// Signatures are gated: the driver cannot load a trip until the server confirms the
// PNG reached Reservation_File. So unlike the fire-and-forget actions above, each send
// keeps a deferred here until `signature_received` (resolve) or `itinerary_error`
// (reject) arrives. Keyed by tripId because the server does not echo messageId back on
// signature_received — it returns { tripId, tripNo, reservationFileId }.
//
// Deliberately separate from `pendingActions`: handleItineraryUpdate clears that whole
// map on any route update, which would settle a signature that is still in flight.
const pendingSignatures = new Map();

// Pending marketplace claims (`pullTrip`), keyed by tripId.
//
// A claim is first-come-first-served, so it must be confirmed by the live server before
// the UI treats the trip as the driver's — never optimistically and never queued offline.
// Each send keeps a deferred here until itinerary_ack (resolve) or itinerary_error
// (reject) arrives for that trip. Kept separate from `pendingActions` for the same reason
// as `pendingSignatures`: handleItineraryUpdate clears that whole map on any route update.
const pendingClaims = new Map();

// Route update callback reference for timeout fallback
let routeUpdateCallback = null;

// Current route data for optimistic updates
let currentRouteData = null;

/**
 * Settle a pending signature send exactly once, clearing its timeout.
 * No-op when nothing is pending for that trip (duplicate/late server event).
 * @param {string|number} tripId
 * @param {'resolve'|'reject'} outcome
 * @param {*} payload - ack response, or an Error when rejecting
 */
function settlePendingSignature(tripId, outcome, payload) {
  if (tripId === undefined || tripId === null) return false;
  const key = String(tripId);
  const pending = pendingSignatures.get(key);
  if (!pending) return false;

  pendingSignatures.delete(key);
  clearTimeout(pending.timeoutId);
  if (outcome === 'resolve') {
    pending.resolve(payload);
  } else {
    pending.reject(payload);
  }
  return true;
}

/**
 * Settle a pending marketplace claim exactly once, clearing its timeout.
 * No-op when nothing is pending for that trip (duplicate/late server event).
 * @param {string|number} tripId
 * @param {'resolve'|'reject'} outcome
 * @param {*} payload - ack response, or an Error when rejecting
 */
// Pending full-fare CASH/ACCOUNT payments (`payment` action), keyed by tripId. The
// server acks with itinerary_ack { action:'payment', tripId } or rejects on
// itinerary_error { action:'payment' }; nothing is marked paid locally before that.
const pendingPayments = new Map();
const PAYMENT_ACK_TIMEOUT_MS = 15000;

function settlePendingPayment(tripId, outcome, payload) {
  if (tripId === undefined || tripId === null) return false;
  const key = String(tripId);
  const pending = pendingPayments.get(key);
  if (!pending) return false;
  clearTimeout(pending.timeoutId);
  pendingPayments.delete(key);
  if (outcome === 'resolve') pending.resolve(payload);
  else pending.reject(payload instanceof Error ? payload : new Error(String(payload)));
  return true;
}

function settlePendingClaim(tripId, outcome, payload) {
  if (tripId === undefined || tripId === null) return false;
  const key = String(tripId);
  const pending = pendingClaims.get(key);
  if (!pending) return false;

  pendingClaims.delete(key);
  clearTimeout(pending.timeoutId);
  if (outcome === 'resolve') {
    pending.resolve(payload);
  } else {
    pending.reject(payload);
  }
  return true;
}

/**
 * Clear stale ITINERARY_ACTION from the offline queue
 * Called when fresh server data is received, indicating queued actions may be redundant
 */
function clearStaleItineraryActions() {
  const pending = offlineStorage.getPendingActions();
  const itineraryActions = pending.filter(a => a.type === 'ITINERARY_ACTION');
  
  if (itineraryActions.length > 0) {
    console.log(`[ItinerarySocket] Clearing ${itineraryActions.length} stale itinerary actions after server update`);
    itineraryActions.forEach(action => {
      offlineStorage.removeAction(action.id);
    });
  }
}

/**
 * Initialize socket listeners for itinerary updates
 * @param {Object} socket - Socket.IO instance
 * @param {Function} onRouteUpdate - Callback when complete route update is received
 * @returns {Function} Cleanup function to remove listeners
 */
export const initializeItinerarySocket = (socket, onRouteUpdate) => {
  if (!socket) {
    console.error('Socket not provided to initializeItinerarySocket');
    return () => {};
  }

  const currentSocketId = socket.id || 'pending';
  
  // Check if same socket is already initialized
  if (initializedSocketId === currentSocketId && currentSocketId !== 'pending') {
    console.log('Itinerary socket already initialized for socket:', currentSocketId);
    return () => {};
  }

  // If different socket, clean up old listeners first
  if (initializedSocketId && initializedSocketId !== currentSocketId) {
    console.log('Socket instance changed, re-initializing listeners...');
  }

  console.log('Initializing itinerary socket listeners for socket:', currentSocketId);
  initializedSocketId = currentSocketId;
  routeUpdateCallback = onRouteUpdate;

  // Listen for complete route updates from server (after any action)
  const handleItineraryUpdate = (response) => {
    console.log('Received itinerary_update from server:', response);
    
    // Clear all pending action timeouts since we received an update
    pendingActions.forEach((timeoutId, messageId) => {
      clearTimeout(timeoutId);
      pendingActions.delete(messageId);
    });
    
    if (response.success && response.routeData) {
      console.log('Route update received - trips:', response.routeData.trips?.length || 0, 
                  'itinerary steps:', response.routeData.itinerary?.length || 0);
      
      // Persist route data to localStorage for session recovery
      comms.persistRouteData(response.routeData);
      
      // Clear any stale ITINERARY_ACTION from the offline queue since server has fresh data
      // This prevents re-syncing actions that the server already processed
      clearStaleItineraryActions();
      
      // Pass complete updated route to parent component
      // Mark as driver-initiated update (not from cross-room broadcaster)
      onRouteUpdate(response.routeData, { source: 'itinerary_update', isExternal: false });
    } else {
      console.error('Itinerary update failed:', response.error);
      alert(`Update failed: ${response.error || 'Unknown error'}`);
    }
  };

  // Listen for simple acknowledgments (for actions that don't trigger full route recalculation)
  const handleItineraryAck = (response) => {
    console.log('Received itinerary_ack from server:', response);
    
    // Clear pending action timeout for this acknowledgment
    if (response.messageId) {
      const timeoutId = pendingActions.get(response.messageId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        pendingActions.delete(response.messageId);
      }
    }
    
    // A marketplace claim is awaited by the Marketplace screen, which surfaces the
    // outcome inline (and refreshes the pool). Settle it here instead of alerting.
    if (response.action === 'pullTrip') {
      const settled = settlePendingClaim(
        response.tripId,
        response.success ? 'resolve' : 'reject',
        response.success ? response : new Error(response.error || 'Could not claim trip.')
      );
      if (settled) return;
    }

    // A full-fare payment is awaited by the Collect Payment sheet, which surfaces the
    // outcome inline and only then offers completion.
    if (response.action === 'payment') {
      const settled = settlePendingPayment(
        response.tripId,
        response.success ? 'resolve' : 'reject',
        response.success ? response : new Error(response.error || 'Payment was not recorded.')
      );
      if (settled) return;
    }

    if (!response.success) {
      console.error('Action acknowledgment failed:', response.error);
      alert(`Action failed: ${response.error || 'Unknown error'}`);
    }
  };
  
  // Handle socket reconnection - rejoin route room and refresh data
  const handleReconnect = (attemptNumber) => {
    console.log('Itinerary socket reconnected after', attemptNumber, 'attempts');
    // Room rejoining is handled by comms.js joinDriverAppRoom and joinRouteRoom
  };

  // DECEMBER 2025: Handle routeUpdate event from server (authoritative route data)
  // This is sent by the dashboard/server when route data changes (e.g., dispatch console updates, re-optimization)
  const handleRouteUpdate = (response) => {
    console.log('Received routeUpdate from server:', response);
    
    // Clear all pending action timeouts since we received an update
    pendingActions.forEach((timeoutId, messageId) => {
      clearTimeout(timeoutId);
      pendingActions.delete(messageId);
    });
    
    if (response.success && response.routeData) {
      console.log('routeUpdate received - trips:', response.routeData.trips?.length || 0, 
                  'itinerary steps:', response.routeData.itinerary?.length || 0,
                  'source:', response.source || 'unknown');
      
      // Persist route data to localStorage for session recovery
      comms.persistRouteData(response.routeData);
      
      // Pass complete updated route to parent component
      // This is authoritative data from the server - it should replace the current state
      // Mark as external update from cross-room broadcaster
      onRouteUpdate(response.routeData, { source: 'routeUpdate', isExternal: true });
    } else if (response.routeData) {
      // Handle case where success flag might be missing but routeData exists
      console.log('routeUpdate received (no success flag) - trips:', response.routeData.trips?.length || 0, 
                  'itinerary steps:', response.routeData.itinerary?.length || 0);
      
      comms.persistRouteData(response.routeData);
      // Mark as external update from cross-room broadcaster
      onRouteUpdate(response.routeData, { source: 'routeUpdate', isExternal: true });
    } else {
      console.warn('routeUpdate received without valid routeData:', response);
    }
  };

  // Server confirmation that a signature PNG was written to Reservation_File.
  // Resolves the promise sendSignature() handed the pickup modal, which is what
  // unlocks the Pickup button.
  const handleSignatureReceived = (response) => {
    console.log('Received signature_received from server:', response);
    const settled = settlePendingSignature(response && response.tripId, 'resolve', response);
    if (!settled) {
      console.warn('signature_received had no pending signature to settle:', response);
    }
  };

  // FEB 2026: Handle itinerary_error events from server
  // Server sends this when an action fails validation or processing
  const handleItineraryError = (response) => {
    console.error('Received itinerary_error from server:', response);

    // Clear pending action timeout for this error
    if (response.messageId) {
      const timeoutId = pendingActions.get(response.messageId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        pendingActions.delete(response.messageId);
      }
    }

    // Construct a user-friendly error message
    const action = response.action || 'action';
    const errorMsg = response.error || response.message || 'Unknown error occurred';
    const details = response.details ? `\n\nDetails: ${response.details}` : '';

    console.error(`Itinerary ${action} failed:`, errorMsg, response);

    // A failed signature is surfaced inline by the pickup modal (which is awaiting
    // this rejection), so skip the generic alert and let the modal show its Retry.
    if (action === 'signature') {
      const settled = settlePendingSignature(response.tripId, 'reject', new Error(errorMsg));
      if (settled) return;
      // No pending entry: fall through so a stray failure is not swallowed entirely.
    }

    // Same for a claim — losing the race for a trip is an ordinary outcome the
    // Marketplace screen reports inline, not an alert-worthy failure.
    //
    // The server's pullTrip failure path does not echo tripId back (unlike its success
    // ack), so fall back to the sole in-flight claim when it is unambiguous. Without
    // this the promise hangs until its own timeout AND the driver gets a raw alert.
    if (action === 'payment') {
      let settled = settlePendingPayment(response.tripId, 'reject', new Error(errorMsg));
      if (!settled && pendingPayments.size === 1) {
        const onlyTripId = pendingPayments.keys().next().value;
        settled = settlePendingPayment(onlyTripId, 'reject', new Error(errorMsg));
      }
      if (settled) return;
    }

    if (action === 'pullTrip') {
      let settled = settlePendingClaim(response.tripId, 'reject', new Error(errorMsg));
      if (!settled && pendingClaims.size === 1) {
        const onlyTripId = pendingClaims.keys().next().value;
        settled = settlePendingClaim(onlyTripId, 'reject', new Error(errorMsg));
      }
      if (settled) return;
    }

    alert(`${action} failed: ${errorMsg}${details}`);
  };
 
  // Attach listeners (remove any existing first to prevent duplicates)
  socket.off('itinerary_update', handleItineraryUpdate);
  socket.off('itinerary_ack', handleItineraryAck);
  socket.off('reconnect', handleReconnect);
  socket.off('routeUpdate', handleRouteUpdate);
  socket.off('itinerary_error', handleItineraryError);
  socket.off('signature_received', handleSignatureReceived);

  socket.on('itinerary_update', handleItineraryUpdate);
  socket.on('itinerary_ack', handleItineraryAck);
  socket.on('reconnect', handleReconnect);
  socket.on('routeUpdate', handleRouteUpdate);
  socket.on('itinerary_error', handleItineraryError);
  socket.on('signature_received', handleSignatureReceived);

  // Return cleanup function
  return () => {
    console.log('Cleaning up itinerary socket listeners...');
    socket.off('itinerary_update', handleItineraryUpdate);
    socket.off('itinerary_ack', handleItineraryAck);
    socket.off('reconnect', handleReconnect);
    socket.off('routeUpdate', handleRouteUpdate);
    socket.off('itinerary_error', handleItineraryError);
    socket.off('signature_received', handleSignatureReceived);
    initializedSocketId = null;
    routeUpdateCallback = null;
    // Clear any pending action timeouts
    pendingActions.forEach((timeoutId) => clearTimeout(timeoutId));
    pendingActions.clear();
    // Reject in-flight signatures: with the listener gone their ack can never arrive,
    // so leaving them pending would hang the modal on "Saving…" until its own timeout.
    pendingSignatures.forEach((pending) => {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error('Connection closed before the signature was confirmed. Please try again.'));
    });
    pendingSignatures.clear();
    // Same for in-flight claims — the ack listener is gone, so the Claim button would
    // otherwise sit disabled until the send's own timeout elapsed.
    pendingClaims.forEach((pending) => {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error('Connection closed before the claim was confirmed. Please try again.'));
    });
    pendingClaims.clear();
    // And in-flight payments — never leave the sheet on "Recording cash…" for an ack
    // that can no longer arrive.
    pendingPayments.forEach((pending) => {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error('Connection closed before the payment was confirmed. Please try again.'));
    });
    pendingPayments.clear();
  };
};

/**
 * Send ITINERARY_ACTION message to server with offline support
 * If offline, queues the action with original timestamp for later sync
 * @param {string} action - Action type (enRoute, arriveOrigin, load, arriveDestination, complete, noShow, etc.)
 * @param {Object} tripData - Trip data including TripId, VehicleId, RouteId
 * @param {Object} additionalData - Optional additional data for specific actions
 * @param {Object} options - Optional options
 * @param {Function} options.onOptimisticUpdate - Callback to apply optimistic UI update
 */
export const sendItineraryAction = (action, tripData, additionalData = {}, options = {}) => {
  const messageId = generateGuid();
  const routeId = tripData.RouteId || tripData.routeId;
  
  // CRITICAL: Capture actual time when action occurred (not when synced)
  // Server will use this timestamp for database recording
  const actionTimestamp = Date.now();
  const actualDateTime = new Date(actionTimestamp).toISOString();
  
  const messageData = {
    tripId: tripData.TripId || tripData.tripId,
    vehicleId: tripData.VehicleId || tripData.vehicleId,
    routeId: routeId,
    actualDateTime: actualDateTime,
    _originalTimestamp: actionTimestamp, // Unix timestamp for server to use
    ...additionalData
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: action,
    ...messageData
  })}`;

  console.log(`Sending ITINERARY_ACTION ${action}:`, messageData);
  
  // Apply optimistic update immediately for responsive UI (ALWAYS, not just offline)
  // Server response will overwrite this when it arrives
  applyOptimisticItineraryUpdate(action, tripData, additionalData);
  
  // Also call custom callback if provided
  if (options.onOptimisticUpdate) {
    options.onOptimisticUpdate(action, tripData, additionalData);
  }

  // Check if online and socket connected
  const socket = comms.getSocket();
  if (socket && socket.connected) {
    // Online: send immediately
    comms.sendSocket(message);
    
    // Set timeout fallback - if no response received, manually refresh route data
    const timeoutId = setTimeout(async () => {
      console.warn(`No response received for action ${action} after ${ACTION_TIMEOUT_MS}ms, triggering manual refresh...`);
      pendingActions.delete(messageId);
      
      // Attempt to manually refresh route data
      if (routeId && routeUpdateCallback) {
        try {
          const refreshedRouteData = await comms.getRouteDetails(routeId);
          if (refreshedRouteData) {
            console.log('Manual route refresh successful');
            comms.persistRouteData(refreshedRouteData);
            routeUpdateCallback(refreshedRouteData);
          }
        } catch (err) {
          console.error('Manual route refresh failed:', err);
        }
      }
    }, ACTION_TIMEOUT_MS);
    
    pendingActions.set(messageId, timeoutId);
  } else {
    // Offline: Queue for later sync with original timestamp
    console.log(`Offline - queueing ITINERARY_ACTION ${action} for later sync`);
    offlineStorage.queueAction({
      type: 'ITINERARY_ACTION',
      data: {
        action,
        messageId,
        ...messageData
      },
      messageFormat: message,
      timestamp: actionTimestamp, // When action actually occurred
    });
  }
};

/**
 * Send enRoute action - driver is heading to pickup
 */
export const sendEnRoute = (tripData) => {
  sendItineraryAction('enRoute', tripData);
};

/**
 * Send arriveOrigin action - driver arrived at pickup location
 */
export const sendArriveOrigin = (tripData) => {
  sendItineraryAction('arriveOrigin', tripData);
};

/**
 * Send load action - passenger loaded
 */
export const sendLoad = (tripData) => {
  sendItineraryAction('load', tripData);
};

/**
 * Send arriveDestination action - driver arrived at dropoff location
 */
export const sendArriveDestination = (tripData) => {
  sendItineraryAction('arriveDestination', tripData);
};

/**
 * Send complete action - trip completed
 */
export const sendComplete = (tripData) => {
  sendItineraryAction('complete', tripData);
};

/**
 * Send noShow action - passenger no-show
 */
export const sendNoShow = (tripData) => {
  sendItineraryAction('noShow', tripData);
};

/**
 * Send unNoShow action - reverse a no-show
 */
export const sendUnNoShow = (tripData) => {
  sendItineraryAction('unNoShow', tripData);
};

/**
 * Send callout action - request passenger notification
 */
export const sendCallout = (tripData) => {
  sendItineraryAction('callout', tripData);
};

/**
 * Send location update action - update driver GPS coordinates
 */
export const sendLocation = (tripData, location) => {
  sendItineraryAction('location', tripData, {
    latitude: location.lat,
    longitude: location.lng
  });
};

/**
 * Send signature action - store digital signature.
 *
 * Unlike the other actions this AWAITS the server. A signature-required trip must not be
 * loadable until the PNG is actually in Reservation_File, so the caller (the pickup modal)
 * only unlocks its Pickup button once this promise resolves.
 *
 * It deliberately does NOT go through sendItineraryAction:
 *  - a signature changes no step state, so the unconditional optimistic update there is a
 *    wasted deep clone that also marks the route optimistically dirty;
 *  - the server replies with `signature_received` rather than a tracked `itinerary_ack`, so
 *    that helper's 10s fallback fired on every send and triggered a spurious route refetch;
 *  - offline it queues and returns normally, which would unlock Pickup with nothing stored.
 *
 * @param {Object} tripData - { tripId|TripId, vehicleId|VehicleId, routeId|RouteId }
 * @returns {Promise<Object>} resolves with { success, action, tripId, tripNo, reservationFileId }
 *   rejects if the server reports an error, nothing arrives within ACTION_TIMEOUT_MS, the
 *   socket is disconnected, or tripId is missing.
 */
export const sendSignature = (tripData, signatureData) => {
  const tripId = tripData.TripId || tripData.tripId;

  if (!tripId) {
    return Promise.reject(new Error('Cannot save signature: trip ID is missing.'));
  }
  if (!signatureData) {
    return Promise.reject(new Error('Cannot save signature: no signature data captured.'));
  }

  // Offline: reject instead of queueing. A queued signature would let the driver load the
  // trip with nothing stored, which is exactly the guarantee this gate exists to provide.
  const socket = comms.getSocket();
  if (!socket || !socket.connected) {
    return Promise.reject(new Error('No connection. Reconnect to save the signature before loading this trip.'));
  }

  // One in-flight signature per trip. A rapid second Confirm/Retry supersedes the first
  // rather than stacking, so the ack cannot settle a stale deferred.
  settlePendingSignature(tripId, 'reject', new Error('Superseded by a newer signature attempt.'));

  const messageId = generateGuid();
  const actionTimestamp = Date.now();
  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'signature',
    tripId,
    vehicleId: tripData.VehicleId || tripData.vehicleId,
    routeId: tripData.RouteId || tripData.routeId,
    actualDateTime: new Date(actionTimestamp).toISOString(),
    _originalTimestamp: actionTimestamp,
    signature: signatureData
  })}`;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingSignatures.delete(String(tripId));
      reject(new Error('The server did not confirm the signature in time. Please try again.'));
    }, ACTION_TIMEOUT_MS);

    pendingSignatures.set(String(tripId), { resolve, reject, timeoutId, messageId });

    try {
      console.log('Sending ITINERARY_ACTION signature for trip', tripId);
      comms.sendSocket(message);
    } catch (err) {
      settlePendingSignature(tripId, 'reject', err instanceof Error ? err : new Error(String(err)));
    }
  });
};

/**
 * Claim an unassigned marketplace trip (`pullTrip`).
 *
 * Claim has no REST endpoint — it exists only as an itinerary socket action. It is
 * first-come-first-served, so this resolves only once the server confirms the
 * assignment and rejects when the trip was already taken (or the feature is off).
 * Never queued offline: a claim that lands minutes later is meaningless.
 *
 * @param {Object} params
 * @param {string} params.tripId
 * @param {string} params.driverId
 * @param {string} [params.vehicleId]
 * @param {string} [params.serviceDate] - fleet-local YYYY-MM-DD
 * @param {string} [params.routeId] - target route for a routed claim, when known
 * @returns {Promise<Object>} the itinerary_ack payload on success
 */
export const sendPullTrip = ({ tripId, driverId, vehicleId, serviceDate, routeId } = {}) => {
  if (!tripId) {
    return Promise.reject(new Error('Cannot claim trip: trip ID is missing.'));
  }
  if (!driverId) {
    return Promise.reject(new Error('Cannot claim trip: driver not identified.'));
  }

  const socket = comms.getSocket();
  if (!socket || !socket.connected) {
    return Promise.reject(new Error('No connection. Reconnect to claim this trip.'));
  }

  // One in-flight claim per trip; a rapid second tap supersedes the first rather than
  // stacking, so a stale ack cannot settle the newer attempt.
  settlePendingClaim(tripId, 'reject', new Error('Superseded by a newer claim attempt.'));

  const messageId = generateGuid();
  const payload = { action: 'pullTrip', tripId, driverId, messageId };
  if (vehicleId) payload.vehicleId = vehicleId;
  if (serviceDate) payload.serviceDate = serviceDate;
  // Target route for a routed claim. Sent when known (the trip's own route, else the
  // driver's active route); the server resolves/ignores it for live-dispatch work.
  if (routeId) payload.routeId = routeId;

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify(payload)}`;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingClaims.delete(String(tripId));
      reject(new Error('The server did not confirm the claim in time. Please try again.'));
    }, ACTION_TIMEOUT_MS);

    pendingClaims.set(String(tripId), { resolve, reject, timeoutId, messageId });

    try {
      console.log('Sending ITINERARY_ACTION pullTrip for trip', tripId);
      comms.sendSocket(message);
    } catch (err) {
      settlePendingClaim(tripId, 'reject', err instanceof Error ? err : new Error(String(err)));
    }
  });
};

/**
 * Full-fare CASH/ACCOUNT selection through the acknowledged Itinerary action channel
 * (PAYMENT_DRIVERAPP_HANDOFF.md §6.3; DRIVERAPP_HOSTED_PAGE_PAYMENTS.md §"Itinerary payment
 * flow"). Online and NON-optimistic: it is never queued offline and nothing changes
 * locally until the server's `itinerary_ack { action:'payment' }` arrives. CREDIT/card
 * must use hosted collection (comms.requestTripCollection), never this action. Live
 * Dispatch's EPMTM is never sent from Itinerary.
 *
 * @param {Object} tripData  { tripId|TripId, vehicleId|VehicleId, routeId|RouteId }
 * @param {'CASH'|'ACCOUNT'} paymentType
 * @param {number} amount    Final fare actually collected (> 0)
 * @param {string} [details]
 * @returns {Promise<Object>} resolves with the ack ({ success, action:'payment', tripId,
 *   routeId, status:'Settled', paymentType, amount }); rejects on itinerary_error
 *   (PAYMENT_INVALID / PAYMENT_COLLECTION_REQUIRED / PAYMENT_FAILED), timeout, disconnect
 *   or a missing trip id.
 */
export const sendPayment = (tripData, paymentType, amount, details = '') => {
  const tripId = tripData && (tripData.TripId || tripData.tripId);
  const type = String(paymentType || '').trim().toUpperCase();
  if (!tripId) return Promise.reject(new Error('Cannot record payment: trip ID is missing.'));
  if (type !== 'CASH' && type !== 'ACCOUNT') {
    return Promise.reject(new Error('Payment type must be CASH or ACCOUNT; card payments use the secure card page.'));
  }
  const paymentAmount = Math.round(Number(amount) * 100) / 100;
  if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
    return Promise.reject(new Error('Payment amount must be greater than zero.'));
  }
  const socket = comms.getSocket();
  if (!socket || !socket.connected) {
    return Promise.reject(new Error('Not connected to dispatch. Reconnect and try again.'));
  }

  settlePendingPayment(tripId, 'reject', new Error('Superseded by a newer payment attempt.'));

  const messageId = generateGuid();
  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'payment',
    tripId,
    vehicleId: tripData.VehicleId || tripData.vehicleId,
    routeId: tripData.RouteId || tripData.routeId,
    actualDateTime: new Date().toISOString(),
    paymentType: type,
    paymentAmount,
    paymentDetails: typeof details === 'string' ? details : '',
  })}`;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingPayments.delete(String(tripId));
      reject(new Error('Dispatch did not confirm the payment. Check the trip status and try again.'));
    }, PAYMENT_ACK_TIMEOUT_MS);
    pendingPayments.set(String(tripId), { resolve, reject, timeoutId, messageId });
    try {
      console.log('Sending ITINERARY_ACTION payment for trip', tripId, type, paymentAmount);
      comms.sendSocket(message, { skipOfflineQueue: true });
    } catch (err) {
      settlePendingPayment(tripId, 'reject', err instanceof Error ? err : new Error(String(err)));
    }
  });
};

/**
 * Send copay action - process copayment
 */
export const sendCopay = (tripData, copayAmount) => {
  sendItineraryAction('copay', tripData, {
    amount: copayAmount
  });
};

/**
 * Send copayConfirm action - confirm copayment collected
 * Alias for sendCopay for clearer semantic meaning
 */
export const sendCopayConfirm = (tripData, copayAmount) => {
  sendItineraryAction('copayConfirm', tripData, {
    amount: copayAmount
  });
};

/**
 * Send createDemandResponseTrip action - create a new trip for demand response mode
 * Origin is the hub location (where passenger boarded), destination is driver's current GPS (where exiting)
 * @param {Object} tripData - Trip creation data
 * @param {string} tripData.routeId - Route ID
 * @param {string} tripData.vehicleId - Vehicle ID
 * @param {string} tripData.driverId - Driver ID
 * @param {number} tripData.passengerCount - Number of passengers
 * @param {string} tripData.passengerName - Passenger name (optional, defaults to 'Demand Response')
 * @param {number} tripData.originLat - Origin latitude (hub where passenger boarded), or tripData.baseLat
 * @param {number} tripData.originLon - Origin longitude (hub where passenger boarded), or tripData.baseLon
 * @param {string} tripData.originTime - ISO timestamp when passenger boarded at hub
 * @param {number} tripData.destinationLat - Destination latitude (where passenger exiting), or tripData.currentLat
 * @param {number} tripData.destinationLon - Destination longitude (where passenger exiting), or tripData.currentLon
 * @param {string} tripData.destinationTime - ISO timestamp when passenger exited (defaults to now)
 */
export const sendCreateDemandResponseTrip = (tripData) => {
  const messageId = generateGuid();
  
  const messageData = {
    action: 'createDemandResponseTrip',
    routeId: tripData.routeId,
    vehicleId: tripData.vehicleId,
    driverId: tripData.driverId,
    passengerCount: tripData.passengerCount,
    passengerName: tripData.passengerName || 'Demand Response',
    // Origin = hub (where passenger boarded)
    originLat: tripData.originLat || tripData.baseLat,
    originLon: tripData.originLon || tripData.baseLon,
    originTime: tripData.originTime,
    originLandmark: tripData.originLandmark || '',
    originStreetNumber: tripData.originStreetNumber || '0',
    originStreetName: tripData.originStreetName || 'Hub Location',
    originCity: tripData.originCity || '',
    originState: tripData.originState || '',
    originPostalCode: tripData.originPostalCode || '',
    // Destination = current GPS (where passenger exiting)
    destinationLat: tripData.destinationLat || tripData.currentLat,
    destinationLon: tripData.destinationLon || tripData.currentLon,
    destinationTime: tripData.destinationTime || new Date().toISOString(),
    destinationStreetNumber: tripData.destinationStreetNumber || '0',
    destinationStreetName: tripData.destinationStreetName || 'Dropoff Location',
    destinationCity: tripData.destinationCity || '',
    destinationState: tripData.destinationState || '',
    destinationPostalCode: tripData.destinationPostalCode || '',
    // Legacy fields for backward compatibility
    baseLat: tripData.baseLat || tripData.originLat,
    baseLon: tripData.baseLon || tripData.originLon,
    currentLat: tripData.currentLat || tripData.destinationLat,
    currentLon: tripData.currentLon || tripData.destinationLon
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify(messageData)}`;

  console.log('Sending createDemandResponseTrip action:', messageData);
  comms.sendSocket(message);
};

/**
 * Send createOutboundFlagTrip action - create a flag trip for passengers exiting after boarding at hub
 * This is specifically for outbound trips in demand response mode
 * Origin is the hub location (where passenger boarded), destination is driver's current GPS (where exiting)
 * @param {Object} tripData - Trip creation data
 * @param {string} tripData.routeId - Route ID
 * @param {string} tripData.vehicleId - Vehicle ID
 * @param {string} tripData.driverId - Driver ID
 * @param {number} tripData.passengerCount - Number of passengers exiting
 * @param {number} tripData.originLat - Hub latitude (where passenger boarded)
 * @param {number} tripData.originLon - Hub longitude (where passenger boarded)
 * @param {string} tripData.originTime - ISO timestamp when passenger boarded at hub
 * @param {number} tripData.destinationLat - Destination latitude (where passenger exiting)
 * @param {number} tripData.destinationLon - Destination longitude (where passenger exiting)
 * @param {string} tripData.destinationTime - ISO timestamp when passenger exited
 */
export const sendCreateOutboundFlagTrip = (tripData) => {
  const messageId = generateGuid();
  
  const messageData = {
    action: 'createOutboundFlagTrip',
    routeId: tripData.routeId,
    vehicleId: tripData.vehicleId,
    driverId: tripData.driverId,
    passengerCount: tripData.passengerCount,
    // Origin = hub (where passenger boarded)
    originLat: tripData.originLat,
    originLon: tripData.originLon,
    originTime: tripData.originTime,
    // Destination = current GPS (where passenger exiting)
    destinationLat: tripData.destinationLat,
    destinationLon: tripData.destinationLon,
    destinationTime: tripData.destinationTime || new Date().toISOString()
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify(messageData)}`;

  console.log('Sending createOutboundFlagTrip action:', messageData);
  comms.sendSocket(message);
};

// ── Phase B: Itinerary-mode Flag Trip (driver creates an on-the-fly trip) ──────
// Three ITINERARY_ACTION senders. Server replies on dedicated events the modal
// subscribes to: flagTripRates, flagTripQuote, flagTripCreated (+ itinerary_error).

// Action 1 — request the server-resolved rate for a preliminary (pickup-only)
// display. The driver never picks a rate (03-create-and-flag-trip.md); this is
// read-only. data: { vehicleId, originLat, originLon }.
export const sendGetFlagTripRates = (data) => {
  const messageId = generateGuid();
  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({ action: 'getFlagTripRates', ...data })}`;
  console.log('Sending getFlagTripRates action:', data);
  comms.sendSocket(message);
};

// Action 2 — fit-check + price. Rate is resolved server-side from vehicle +
// pickup + dropoff. data: { routeId, vehicleId, mileage, durationSeconds,
// originLat, originLon, destinationLat, destinationLon }.
export const sendQuoteFlagTrip = (data) => {
  const messageId = generateGuid();
  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({ action: 'quoteFlagTrip', ...data })}`;
  console.log('Sending quoteFlagTrip action:', data);
  comms.sendSocket(message);
};

// Action 3 — create the trip (pickup = current GPS, completed; delivery pending).
export const sendCreateItineraryFlagTrip = (data) => {
  const messageId = generateGuid();
  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({ action: 'createItineraryFlagTrip', ...data })}`;
  console.log('Sending createItineraryFlagTrip action:', data);
  comms.sendSocket(message);
};

/**
 * Send startBreak action - mark break step as started
 */
export const sendStartBreak = (stepData, location) => {
  const messageId = generateGuid();
  const actualDateTime = new Date().toISOString();
  
  const messageData = {
    stepId: stepData.StepId,
    vehicleId: stepData.vehicleId,
    routeId: stepData.routeId,
    latitude: location.lat,
    longitude: location.lng,
    actualDateTime: actualDateTime
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'startBreak',
    ...messageData
  })}`;

  console.log('Sending startBreak action:', messageData);
  comms.sendSocket(message);
};

/**
 * Send completeBreak action - mark break step as completed
 */
export const sendCompleteBreak = (stepData, location) => {
  const messageId = generateGuid();
  const actualDateTime = new Date().toISOString();
  
  const messageData = {
    stepId: stepData.StepId,
    vehicleId: stepData.vehicleId,
    routeId: stepData.routeId,
    latitude: location.lat,
    longitude: location.lng,
    actualDateTime: actualDateTime
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'completeBreak',
    ...messageData
  })}`;

  console.log('Sending completeBreak action:', messageData);
  comms.sendSocket(message);
};

/**
 * Send completeStart action - mark start step as completed
 */
export const sendCompleteStart = (stepData, currentLocation) => {
  const messageId = generateGuid();
  const actualDateTime = new Date().toISOString();
  
  const messageData = {
    stepId: stepData.StepId,
    vehicleId: stepData.vehicleId,
    routeId: stepData.routeId,
    driverId: stepData.driverId,  // Include driverId for vehicle assignment
    latitude: currentLocation.lat,
    longitude: currentLocation.lng,
    actualDateTime: actualDateTime
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'completeStart',
    ...messageData
  })}`;

  console.log('Sending completeStart action:', messageData);
  comms.sendSocket(message);
};

/**
 * Send completeEnd action - mark end step as completed
 */
export const sendCompleteEnd = (stepData, currentLocation) => {
  const messageId = generateGuid();
  const actualDateTime = new Date().toISOString();
  
  const messageData = {
    stepId: stepData.StepId,
    vehicleId: stepData.vehicleId,
    routeId: stepData.routeId,
    latitude: currentLocation.lat,
    longitude: currentLocation.lng,
    actualDateTime: actualDateTime
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'completeEnd',
    ...messageData
  })}`;

  console.log('Sending completeEnd action:', messageData);
  comms.sendSocket(message);
};

/**
 * Send completeSplitShiftBreak action - mark split shift break step as completed
 * This triggers a "clock out" action similar to completeEnd but preserves route progress
 * Driver can re-authorize later and resume the same route
 */
export const sendCompleteSplitShiftBreak = (stepData, currentLocation) => {
  const messageId = generateGuid();
  const actualDateTime = new Date().toISOString();
  
  const messageData = {
    stepId: stepData.StepId,
    vehicleId: stepData.vehicleId,
    routeId: stepData.routeId,
    latitude: currentLocation.lat,
    longitude: currentLocation.lng,
    actualDateTime: actualDateTime
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'completeSplitShiftBreak',
    ...messageData
  })}`;

  console.log('Sending completeSplitShiftBreak action:', messageData);
  comms.sendSocket(message);
};

/**
 * Send requestSplitShift action - request approval for a split shift break
 * This sets the break step status to 'splitShiftRequested' and notifies the dashboard
 * Dashboard user must approve or reject the request
 */
export const sendRequestSplitShift = (stepData, currentLocation) => {
  const messageId = generateGuid();
  const actualDateTime = new Date().toISOString();
  
  const messageData = {
    stepId: stepData.StepId || stepData.stepId,
    vehicleId: stepData.vehicleId,
    routeId: stepData.routeId,
    latitude: currentLocation.lat,
    longitude: currentLocation.lng,
    actualDateTime: actualDateTime
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'requestSplitShift',
    ...messageData
  })}`;

  console.log('Sending requestSplitShift action:', messageData);
  comms.sendSocket(message);
};

/**
 * Send departHub action - record passenger count when departing hub in demand response mode
 * Updates the hub step's Load field with passenger count
 * @param {Object} hubData - Hub departure data
 * @param {string} hubData.routeId - Route ID
 * @param {string} hubData.vehicleId - Vehicle ID
 * @param {string} hubData.stepId - Optional specific hub step ID
 * @param {number} passengerCount - Number of passengers boarding
 * @param {Object} currentLocation - Current GPS location {lat, lng}
 */
export const sendDepartHub = (hubData, passengerCount, currentLocation) => {
  const messageId = generateGuid();
  const actualDateTime = new Date().toISOString();
  
  const messageData = {
    routeId: hubData.routeId,
    vehicleId: hubData.vehicleId,
    stepId: hubData.stepId || hubData.StepId,
    passengerCount: passengerCount,
    latitude: currentLocation?.lat,
    longitude: currentLocation?.lng,
    actualDateTime: actualDateTime
  };

  const message = `${messageId}|ITINERARY_ACTION|${JSON.stringify({
    action: 'departHub',
    ...messageData
  })}`;

  console.log('Sending departHub action:', messageData);
  comms.sendSocket(message);
};


/**
 * Helper function to determine trip status for UI display
 */
export const getTripStatus = (trip) => {
  if (!trip || !trip.Status) return 'pending';
  
  const status = trip.Status.toLowerCase();
  
  // Map trip statuses to UI states
  if (status.includes('complete') || status.includes('closed')) {
    return 'completed';
  }
  
  if (status.includes('loaded') || 
      status.includes('enroute') || 
      status.includes('arrived')) {
    return 'active';
  }
  
  return 'pending';
};

/**
 * Helper function to determine if trip can perform action
 */
export const canPerformAction = (trip, action) => {
  if (!trip || !trip.Status) return false;
  
  const status = trip.Status.toLowerCase();
  
  switch (action) {
    case 'enRoute':
      return status.includes('assign') || status.includes('dispatch');
      
    case 'arriveOrigin':
      return status.includes('enroute');
      
    case 'load':
      return status.includes('arrivedorigin');
      
    case 'arriveDestination':
      return status.includes('loaded');
      
    case 'complete':
      return status.includes('arriveddestination');
      
    case 'noShow':
      return status.includes('arrivedorigin') || status.includes('enroute');
      
    case 'unNoShow':
      return status.includes('noshow');
      
    default:
      return false;
  }
};

/**
 * Generate GUID for message IDs
 */
function generateGuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Apply optimistic update to local route data when offline
 * Updates local state to reflect the action immediately, even without server confirmation
 * Server data will overwrite this when connection is restored (server-wins)
 * 
 * @param {string} action - Action type
 * @param {Object} tripData - Trip data
 * @param {Object} additionalData - Additional action data
 */
function applyOptimisticItineraryUpdate(action, tripData, additionalData) {
  const tripId = tripData.TripId || tripData.tripId;
  const stepId = tripData.StepId || tripData.stepId;
  const timestamp = new Date().toISOString();
  
  console.log(`[Optimistic Update] Applying ${action} for tripId: ${tripId}, stepId: ${stepId}`);
  
  offlineStorage.applyOptimisticUpdate((routeData) => {
    if (!routeData) {
      console.warn('[Optimistic Update] No route data in storage');
      return routeData;
    }
    
    // Deep clone to avoid mutation issues
    const updatedRouteData = JSON.parse(JSON.stringify(routeData));
    
    // Update itinerary steps
    if (updatedRouteData.itinerary && Array.isArray(updatedRouteData.itinerary)) {
      console.log(`[Optimistic Update] Processing ${updatedRouteData.itinerary.length} itinerary steps`);
      console.log(`[Optimistic Update] Looking for tripId: ${tripId}`);
      console.log('[Optimistic Update] First 3 steps:', updatedRouteData.itinerary.slice(0, 3).map(s => ({
        TripId: s.TripId,
        tripId: s.tripId,
        StepDescription: s.StepDescription,
        type: s.type,
        isCompleted: s.isCompleted
      })));
      
      updatedRouteData.itinerary = updatedRouteData.itinerary.map(step => {
        const stepTripId = step.TripId || step.tripId;
        const currentStepId = step.StepId || step.stepId;
        
        // For trip-based actions, match by tripId AND step type
        // For step-based actions (break, start, end), match by stepId
        const stepType = (step.StepDescription || step.type || '').toLowerCase();
        
        // Log matching attempts for debugging
        if (stepTripId === tripId) {
          console.log(`[Optimistic Update] Found matching tripId. StepType: ${stepType}, Action: ${action}`);
        }
        
        const updatedStep = { ...step };
        
        // Mark step based on action - emulate online behavior
        switch (action) {
          case 'arriveOrigin':
            // Mark PICKUP step as ARRIVED (not completed) - show modal for Load/NoShow
            if (stepTripId === tripId && stepType === 'pickup') {
              console.log(`[Optimistic Update] Marking PICKUP step as ARRIVED for trip ${tripId}`);
              updatedStep.StepStatus = 'arrived';
              updatedStep.isArrived = true;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'load':
            // Mark PICKUP step as completed (passenger loaded)
            if (stepTripId === tripId && stepType === 'pickup') {
              console.log(`[Optimistic Update] Marking PICKUP step as COMPLETED for trip ${tripId}`);
              updatedStep.isCompleted = true;
              updatedStep.completedTime = timestamp;
              updatedStep.ActualDateTime = timestamp;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'arriveDestination':
            // Mark DELIVERY step as ARRIVED (not completed) - show Complete button
            if (stepTripId === tripId && (stepType === 'delivery' || stepType === 'dropoff')) {
              console.log(`[Optimistic Update] Marking DELIVERY step as ARRIVED for trip ${tripId}`);
              updatedStep.StepStatus = 'arrived';
              updatedStep.isArrived = true;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'complete':
            // Mark DELIVERY step as completed (trip finished)
            if (stepTripId === tripId && (stepType === 'delivery' || stepType === 'dropoff')) {
              console.log(`[Optimistic Update] Marking DELIVERY step as COMPLETED for trip ${tripId}`);
              updatedStep.isCompleted = true;
              updatedStep.completedTime = timestamp;
              updatedStep.ActualDateTime = timestamp;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'noShow':
            // Mark BOTH pickup and delivery steps for this trip as completed (no-show ends trip)
            if (stepTripId === tripId) {
              updatedStep.isCompleted = true;
              updatedStep.completedTime = timestamp;
              updatedStep.ActualDateTime = timestamp;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'startBreak':
            // Mark break step as arrived but NOT completed
            if (currentStepId === stepId || (stepType === 'break' && !step.isCompleted)) {
              updatedStep.StepStatus = 'arrived';
              updatedStep.isArrived = true;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'completeBreak':
            // Mark break step as completed
            if (currentStepId === stepId || (stepType === 'break' && step.isArrived && !step.isCompleted)) {
              updatedStep.isCompleted = true;
              updatedStep.completedTime = timestamp;
              updatedStep.ActualDateTime = timestamp;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'completeStart':
            // Mark start step as completed
            if (currentStepId === stepId || stepType === 'start') {
              updatedStep.isCompleted = true;
              updatedStep.completedTime = timestamp;
              updatedStep.ActualDateTime = timestamp;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'completeEnd':
            // Mark end step as completed
            if (currentStepId === stepId || stepType === 'end') {
              updatedStep.isCompleted = true;
              updatedStep.completedTime = timestamp;
              updatedStep.ActualDateTime = timestamp;
              updatedStep._optimistic = true;
            }
            break;
            
          case 'unNoShow':
            // Revert no-show - restore BOTH pickup and delivery steps
            if (stepTripId === tripId) {
              updatedStep.isCompleted = false;
              updatedStep.completedTime = null;
              updatedStep._optimistic = true;
            }
            break;
        }
        
        return updatedStep;
      });
    }
    
    // Update trip status
    if (updatedRouteData.trips && Array.isArray(updatedRouteData.trips)) {
      updatedRouteData.trips = updatedRouteData.trips.map(trip => {
        const tId = trip.Id || trip.id || trip.reservation?.id;
        if (tId !== tripId) return trip;
        
        const updatedTrip = { ...trip };
        
        // Update status based on action
        switch (action) {
          case 'enRoute':
            updatedTrip.Status = 'Dispatched';
            break;
          case 'arriveOrigin':
            updatedTrip.Status = 'ArrivedOrigin';
            break;
          case 'load':
            updatedTrip.Status = 'Loaded';
            break;
          case 'arriveDestination':
            updatedTrip.Status = 'ArrivedDestination';
            break;
          case 'complete':
            updatedTrip.Status = 'Completed';
            break;
          case 'noShow':
            updatedTrip.Status = 'NoShowed';
            break;
          case 'unNoShow':
            updatedTrip.Status = 'Dispatched';
            break;
        }
        
        updatedTrip._optimistic = true;
        return updatedTrip;
      });
    }
    
    // Mark entire route data as having optimistic updates
    updatedRouteData._hasOptimisticUpdates = true;
    updatedRouteData._lastOptimisticUpdate = timestamp;
    
    console.log('[Optimistic Update] Updated itinerary steps:', 
      updatedRouteData.itinerary?.filter(s => s._optimistic).map(s => ({
        stepType: s.StepDescription || s.type,
        tripId: s.TripId || s.tripId,
        isCompleted: s.isCompleted
      }))
    );
    
    return updatedRouteData;
  });
  
  // Also update local callback if available
  if (routeUpdateCallback) {
    const cachedData = offlineStorage.getRouteData();
    if (cachedData) {
      // Verify the update was applied
      const updatedSteps = cachedData.itinerary?.filter(s => s._optimistic);
      console.log('[Optimistic Update] Verified cached data has', updatedSteps?.length || 0, 'optimistic steps');
      console.log('[Optimistic Update] Calling routeUpdateCallback');
      try {
        // Pass metadata to indicate this is an optimistic update (not external)
        routeUpdateCallback(cachedData, { source: 'optimistic', isExternal: false });
        console.log('[Optimistic Update] routeUpdateCallback completed successfully');
      } catch (e) {
        console.error('[Optimistic Update] routeUpdateCallback threw error:', e);
      }
    } else {
      console.warn('[Optimistic Update] No cached data available after update');
    }
  } else {
    console.warn('[Optimistic Update] No routeUpdateCallback registered');
  }
}

/**
 * Set current route data reference for optimistic updates
 */
export const setCurrentRouteData = (routeData) => {
  currentRouteData = routeData;
};

/**
 * Helper function to find current trip from itinerary
 * Returns the first trip that is in active status (not completed)
 * Uses TripId (UUID) matching to reservation.id for consistency
 */
export const findCurrentTrip = (itinerary, trips) => {
  if (!itinerary || !Array.isArray(itinerary) || !trips || !Array.isArray(trips)) {
    return null;
  }

  // Find first incomplete step in itinerary
  const currentStep = itinerary.find(step => !step.completedTime && !step.isCompleted);
  if (!currentStep) return null;

  // Match step to trip by TripId (UUID) to reservation.id or trip.id
  const stepTripId = currentStep.TripId || currentStep.tripId;
  const trip = trips.find(t => {
    return t.reservation?.id === stepTripId || 
           t.id === stepTripId || 
           t.Id === stepTripId;
  });

  return trip || null;
};

/**
 * Helper function to determine current waypoint type from itinerary step
 */
export const getWaypointType = (step) => {
  if (!step) return null;
  
  const stepType = (step.type || '').toLowerCase();
  
  if (stepType === 'pickup' || stepType === 'start') {
    return 'pickup';
  }
  
  if (stepType === 'delivery' || stepType === 'dropoff' || stepType === 'end') {
    return 'dropoff';
  }
  
  if (stepType === 'break') {
    return 'break';
  }
  
  return null;
};

/**
 * Process itinerary steps for display with smart filtering
 * CRITICAL: Implements smart filtering that keeps completed steps appearing AFTER incomplete steps
 * (out-of-order completions) but filters out completed steps before the first incomplete step.
 * This allows completed steps to remain visible in the list until all earlier steps are completed.
 *
 * JUNE 2026: Order strictly by the server-provided Sequence (stopNumber), which is the
 * authoritative VROOM/dispatcher ordering. Falls back to eta only when no valid sequence
 * exists. This also fixes break placement: breaks hold their saved sequence slot instead of
 * being re-positioned by time. (Replaces the FEB 2026 eta sort, which incorrectly assumed
 * StartDateTime and eta were distinct early/late times - the server sends a single value.)
 */
export const processItinerarySteps = (itinerary, trips, options = {}) => {
  const { demandResponseMode = false } = options;
  
  if (!itinerary || !Array.isArray(itinerary)) {
    return [];
  }

  // Filter out invalid steps - these are placeholder/scheduling steps that should not be shown to drivers
  // In demand response mode, include 'hub' steps which indicate return-to-hub events
  const validStepTypes = demandResponseMode 
    ? ['start', 'pickup', 'delivery', 'dropoff', 'break', 'end', 'hub']
    : ['start', 'pickup', 'delivery', 'dropoff', 'break', 'end'];
  
  const filteredItinerary = itinerary.filter(step => {
    const stepType = (step.StepDescription || step.StepName || step.type || '').toLowerCase();
    return validStepTypes.includes(stepType);
  });

  // Resolve the authoritative ordering value for a step. The server populates
  // stopNumber from Route_Step.Sequence (StopSequence). This is the VROOM/dispatcher
  // order and is the single source of truth for step ordering, including breaks.
  const getSequence = (step) => {
    const seq = step.stopNumber ?? step.StopSequence;
    return typeof seq === 'number' && seq > 0 ? seq : 0;
  };

  // Order by Sequence when any step carries a valid sequence; otherwise fall back to eta.
  // This mirrors the server (routes/driver.js loadRouteStepItinerary) so the driver app
  // never reorders steps away from their saved positions.
  const hasValidSequence = filteredItinerary.some(step => getSequence(step) > 0);
  const sortedItinerary = [...filteredItinerary].sort((a, b) => {
    if (hasValidSequence) {
      return getSequence(a) - getSequence(b);
    }
    const etaA = new Date(a.eta || a.StartDateTime).getTime();
    const etaB = new Date(b.eta || b.StartDateTime).getTime();
    return etaA - etaB;
  });

  // Find the first incomplete step index (now using sorted array)
  const firstIncompleteIndex = sortedItinerary.findIndex(step => !step.isCompleted);

  // Smart filtering using sorted array:
  // 1. If all steps are completed, return empty array
  // 2. Otherwise, keep all steps from first incomplete onward (including any completed ones after that point)
  const filteredSteps = firstIncompleteIndex === -1 
    ? [] // All steps completed
    : sortedItinerary.slice(firstIncompleteIndex); // From first incomplete onward

  return filteredSteps.map(step => {
    // Step type from StepDescription (pickup, delivery, break, start, end)
    const stepType = (step.StepDescription || step.StepName || '').toLowerCase();
    
    // Find associated trip by TripId (UUID) matching to reservation.id or trip.id
    const stepTripId = step.TripId || step.tripId;

    const trip = stepTripId ? trips?.find(t => 
      t.reservation?.id === stepTripId || 
      t.id === stepTripId || 
      t.Id === stepTripId
    ) : null;

    // Format address from step or trip data
    let address = '';
    let address2 = '';
    let passengerName = '';
    let phone = '';
    let wheelChair = false;
    
    if (trip) {
      console.log('processItinerarySteps - Found trip for step:', {
        stepTripId: stepTripId,
        stepDescription: step.StepDescription,
        tripReservationId: trip.reservation?.id,
        passengerName: trip.passenger?.name
      });
      
      passengerName = trip.passenger?.name || '';
      phone = trip.passenger?.phone || '';
      wheelChair = trip.reservation?.wheelchair > 0;
      
      if (stepType === 'pickup' || stepType === 'start') {

        address = trip.pickup?.streetNumber + ' ' + (trip.pickup?.streetName || '')
        address2 = (trip.pickup?.city || '') + " " + (trip.pickup?.state || '')
      } else if (stepType === 'delivery' || stepType === 'dropoff') {

        address = trip.dropoff?.streetNumber + ' ' + (trip.dropoff?.streetName || '')
        address2 = (trip.dropoff?.city || '') + " " + (trip.dropoff?.state || '')
      }
    } else {
      console.warn('processItinerarySteps - No trip found for step:', {
        stepSortOrder: step.SortOrder,
        stepType: stepType,
        stepDescription: step.StepDescription,
        availableTrips: trips?.map(t => ({
          reservationNumber: t.reservation?.number,
          passengerName: t.passenger?.name
        }))
      });
    }

    // Format time
    const scheduledTime = step.StartDateTime || step.eta;
    let formattedTime = '';
    if (scheduledTime) {
      try {
        const date = new Date(scheduledTime);
        formattedTime = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
      } catch (e) {
        formattedTime = scheduledTime;
      }
    }

    return {
      // Original step data
      ...step,
      
      // Formatted for UI components (matching LiveDispatch waypoint format)
      type: stepType,
      passengerName: passengerName || 'Driver Determined',
      accountName: trip?.passenger?.account || '',
      address: address || ``,
      address2: address2,
      phone: phone,
      wheelChair: wheelChair,
      time: formattedTime,
      scheduledTime: formattedTime,
      
      // Additional data for socket actions
      vehicleId: null, // Will be set by parent component
      routeId: null, // Will be set by parent component
      
      // Trip reference for extended data
      trip: trip,
      isBreak: step.breakStep === true,
      
      // Status and completion flag
      status: getTripStatus(trip),
      isCompleted: step.isCompleted || false
    };
  });
};

/**
 * Find the current waypoint from processed steps
 * Returns the first INCOMPLETE step (never selects completed steps as currentWaypoint)
 */
export const findCurrentWaypoint = (processedSteps) => {
  if (!processedSteps || processedSteps.length === 0) {
    console.log('[breakETA] findCurrentWaypoint: No processed steps provided');
    return null;
  }
  
  console.log('[breakETA] findCurrentWaypoint: Searching through', processedSteps.length, 'steps');
  console.log('[breakETA] findCurrentWaypoint: Steps order:', processedSteps.map((s, idx) => ({
    idx,
    type: s.type || s.StepDescription,
    eta: s.eta,
    isCompleted: s.isCompleted,
    isBreak: s.isBreak || s.breakStep
  })));
  
  // Find first incomplete step
  const currentWaypoint = processedSteps.find(step => !step.isCompleted);
  
  if (currentWaypoint) {
    console.log('[breakETA] findCurrentWaypoint: Selected waypoint:', {
      type: currentWaypoint.type || currentWaypoint.StepDescription,
      eta: currentWaypoint.eta,
      StartDateTime: currentWaypoint.StartDateTime,
      isBreak: currentWaypoint.isBreak || currentWaypoint.breakStep,
      passengerName: currentWaypoint.passengerName
    });
  } else {
    console.log('[breakETA] findCurrentWaypoint: No incomplete step found');
  }
  
  return currentWaypoint || null;
};
